import { useState, useEffect } from "react";
import { GiTowTruck } from "react-icons/gi";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaBars,
  FaTimes,
  FaMapMarkerAlt,
  FaArrowRight,
  FaClock,
  FaBolt,
  FaCheckCircle,
} from "react-icons/fa";
import Carousel from "react-bootstrap/Carousel";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

// ─── DATA ───────────────────────────────────────────────

const PHONE = "5535988519481";
const PHONE_DISPLAY = "+55 35 98851-9481";
const WHATSAPP = "https://wa.me/553588519481";

const NAV_LINKS = [
  { href: "#fotos", label: "Fotos" },
  { href: "#como-funciona", label: "Como funciona" },
  { href: "#depoimentos", label: "Depoimentos" },
  { href: "#cobertura", label: "Cobertura" },
];

const TICKER_ITEMS = [
  "Disponível 24 horas",
  "Preço justo, sem surpresa",
  "Atendimento rápido",
  "Guincho próprio",
  "Sul de Minas Gerais",
  "Sem burocracia",
];

const STEPS = [
  {
    num: 1,
    title: "Me chama",
    desc: "Pelo telefone ou WhatsApp. Atendo a qualquer hora, todo dia.",
  },
  {
    num: 2,
    title: "Manda o local",
    desc: "Compartilhe sua localização. Uso GPS pra te encontrar rápido.",
  },
  {
    num: 3,
    title: "Já estou indo",
    desc: "Saio na hora pela rota mais rápida. Chego pronto pra resolver.",
  },
  {
    num: 4,
    title: "Tudo certo",
    desc: "Seu veículo cuidado com atenção. Você segue o dia tranquilo.",
  },
];

const TESTIMONIALS = [
  {
    stars: 5,
    text: "Muito bom, recomendo. Precisei, foi muito rápido e atencioso.",
    name: "Cássio Alves",
    city: "Alfenas, MG",
  },
  {
    stars: 5,
    text: "Atende os clientes muito bem e tem um preço bom. O melhor da região.",
    name: "Sidney Diniz",
    city: "Alfenas, MG",
  },
  {
    stars: 5,
    text: "Atendimento nota 1000 com um preço justo. Super recomendo.",
    name: "Danilo Salles",
    city: "Alfenas, MG",
  },
];

const COVERAGE = [
  "Alfenas",
  "Areado",
  "Alterosa",
  "Carmo do Rio Claro",
  "Conceição da Aparecida",
  "Divisa Nova",
  "Fama",
  "Machado",
  "Paraguaçu",
  "Poço Fundo",
  "Serrania",
  "Três Pontas",
  "Campos Gerais",
  "Boa Esperança",
  "Campo do Meio",
];

// ─── HOOKS ──────────────────────────────────────────────

function useScrollReveal(selector) {
  useEffect(() => {
    const els = document.querySelectorAll(selector);
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = "1";
            e.target.style.transform = "translateY(0)";
          }
        }),
      { threshold: 0.12 }
    );
    els.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(24px)";
      el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
      io.observe(el);
    });
    return () => io.disconnect();
  }, [selector]);
}

function getInitials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// ─── COMPONENTS ─────────────────────────────────────────

function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <nav>
        <a href="/" className="logo">
          <img
            src="/favicon_io/android-chrome-192x192.png"
            alt="Rogério Guincho"
            className="logo-img"
          />
          <span className="logo-text">Rogério Guincho</span>
        </a>
        <ul className={open ? "nav-open" : ""}>
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a href={link.href} onClick={() => setOpen(false)}>
                {link.label}
              </a>
            </li>
          ))}
          <li>
            <a
              href="#contato"
              className="nav-cta"
              onClick={() => setOpen(false)}
            >
              <FaPhoneAlt /> Chamar agora
            </a>
          </li>
        </ul>
        <button
          className="hamburger"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <FaTimes /> : <FaBars />}
        </button>
      </nav>
      {open && (
        <div className="nav-overlay" onClick={() => setOpen(false)} />
      )}
    </>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-bg" />
      <div className="hero-content">
        <div className="hero-tag">
          <span className="hero-tag-dot" />
          Disponível agora · Alfenas, MG
        </div>
        <h1>
          Chamou,
          <br />
          <em>eu vou.</em>
        </h1>
        <p className="hero-desc">
          Sou o Rogério, guincheiro em Alfenas e região. Atendo pane, acidente
          ou reboque a qualquer hora, com chegada rápida e preço justo.
        </p>
        <div className="hero-actions">
          <a href={`tel:${PHONE}`} className="hero-phone-cta">
            <div className="hero-phone-cta-icon">
              <FaPhoneAlt />
            </div>
            <div className="hero-phone-cta-text">
              <div className="hero-phone-cta-label">Chamar agora</div>
              <div className="hero-phone-cta-number">{PHONE_DISPLAY}</div>
            </div>
            <FaArrowRight className="hero-phone-cta-arrow" />
          </a>
          <a href={WHATSAPP} className="hero-wa-btn">
            <FaWhatsapp /> WhatsApp
          </a>
        </div>
        <div className="hero-proof">
          <div className="proof-item">
            <span className="proof-item-num">10+</span>
            <span>anos de estrada</span>
          </div>
          <span className="proof-sep" />
          <div className="proof-item">
            <FaClock className="proof-item-icon" />
            <span>24h todo dia</span>
          </div>
          <span className="proof-sep" />
          <div className="proof-item">
            <FaBolt className="proof-item-icon" />
            <span>Atendo na hora</span>
          </div>
        </div>
      </div>
      <div className="hero-visual" aria-hidden="true">
        <div className="hero-card">
          <div className="hero-card-glow" />
          <div className="hero-card-top">
            <div className="hero-card-badge">
              <FaCheckCircle /> Online
            </div>
            <div className="hero-card-time">24h</div>
          </div>
          <GiTowTruck className="hero-card-truck" />
          <div className="hero-card-bottom">
            <div className="hero-card-status">
              <div className="hero-card-status-dot" />
              <div>
                <div className="hero-card-status-label">Status</div>
                <div className="hero-card-status-value">Pronto pra sair</div>
              </div>
            </div>
            <a href={`tel:${PHONE}`} className="hero-card-mini-cta">
              <FaPhoneAlt />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Ticker() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker-wrap">
      <div className="ticker">
        {doubled.map((item, i) => (
          <span key={i}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function Images() {
  return (
    <section className="images-section" id="fotos">
      <div className="images-header">
        <div className="section-label">Galeria</div>
        <h2 className="section-title">O guincho em ação</h2>
        <p className="section-sub">
          Algumas fotos do dia a dia atendendo a região.
        </p>
      </div>
      <div className="carrousel">
        <Carousel interval={4000}>
          {[1, 2, 3, 4].map((n) => (
            <Carousel.Item key={n}>
              <img
                src={`/${n}.png`}
                alt="Guincho em atendimento"
                className="carousel-img"
              />
            </Carousel.Item>
          ))}
        </Carousel>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="how-section" id="como-funciona">
      <div className="how-inner">
        <div className="how-header">
          <div className="section-label">Passo a passo</div>
          <h2>Como funciona</h2>
          <p>
            Em poucos minutos você já tem um guincho a caminho. Simples e sem
            complicação.
          </p>
          <a href={`tel:${PHONE}`} className="how-cta">
            <FaPhoneAlt /> Ligar agora
          </a>
        </div>
        <div className="how-steps">
          {STEPS.map((s) => (
            <div className="how-step" key={s.num}>
              <div className="how-num">{s.num}</div>
              <div className="how-step-body">
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials-section" id="depoimentos">
      <div className="testimonials-inner">
        <div className="testimonials-header">
          <div className="section-label">Depoimentos</div>
          <h2 className="section-title">O que dizem por aí</h2>
          <p className="section-sub">
            Avaliações reais de pessoas que já chamaram.
          </p>
        </div>
        <div className="testimonials-grid">
          {TESTIMONIALS.map((t) => (
            <div className="testimonial-card" key={t.name}>
              <div className="testimonial-stars">{"★".repeat(t.stars)}</div>
              <p>"{t.text}"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{getInitials(t.name)}</div>
                <div className="testimonial-author-info">
                  <div className="testimonial-author-name">{t.name}</div>
                  <div className="testimonial-author-city">{t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaFull() {
  return (
    <section className="cta-section" id="contato">
      <div className="cta-card">
        <div className="cta-card-noise" />
        <div className="cta-heading">
          <div className="section-label cta-label">Precisou? Me chama.</div>
          <h2>
            Estou pronto<br />pra sair agora.
          </h2>
          <p>
            Atendo todo dia, qualquer horário. Pane, acidente ou reboque, é só
            ligar.
          </p>
        </div>
        <div className="cta-contact">
          <a href={`tel:${PHONE}`} className="cta-phone">
            <div className="cta-phone-icon">
              <FaPhoneAlt />
            </div>
            <div className="cta-phone-text">
              <div className="cta-phone-label">Toque para ligar</div>
              <div className="cta-phone-number">{PHONE_DISPLAY}</div>
            </div>
            <FaArrowRight className="cta-phone-arrow" />
          </a>
          <a href={WHATSAPP} className="cta-wa">
            <FaWhatsapp /> Chamar no WhatsApp
          </a>
          <div className="cta-note">
            <FaCheckCircle /> Disponível 24h · Sem taxa de chamada
          </div>
        </div>
      </div>
    </section>
  );
}

function Coverage() {
  return (
    <section className="coverage-section" id="cobertura">
      <div className="coverage-inner">
        <div className="section-label">Cobertura</div>
        <h2 className="coverage-title">Atendo toda a região</h2>
        <p className="coverage-sub">
          Com base em Alfenas, eu atendo toda a região do Sul de Minas Gerais.
          Não vê sua cidade aqui? Me chama mesmo assim.
        </p>
        <div className="coverage-list">
          {COVERAGE.map((city) => (
            <span className="coverage-tag" key={city}>
              <FaMapMarkerAlt className="coverage-pin" />
              {city}
            </span>
          ))}
          <span className="coverage-tag coverage-tag-more">+ outras</span>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-main">
          <div className="footer-brand-block">
            <div className="footer-brand">
              <img
                src="/favicon_io/android-chrome-192x192.png"
                alt="Rogério Guincho"
                className="footer-logo-img"
              />
              <div className="footer-name">Rogério Guincho</div>
            </div>
            <p className="footer-desc">
              Serviço de guincho 24 horas em Alfenas e região do Sul de Minas
              Gerais. Atendimento rápido com preço justo.
            </p>
          </div>
          <div>
            <div className="footer-section-title">Navegação</div>
            <div className="footer-links">
              {NAV_LINKS.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="footer-section-title">Contato</div>
            <div className="footer-contact">
              <a href={`tel:${PHONE}`} className="footer-contact-link">
                <FaPhoneAlt /> {PHONE_DISPLAY}
              </a>
              <a href={WHATSAPP} className="footer-contact-link">
                <FaWhatsapp /> WhatsApp
              </a>
              <span className="footer-contact-meta">Disponível 24 horas</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Rogério Guincho · Todos os direitos reservados</p>
        </div>
      </div>
    </footer>
  );
}

function FloatBtn() {
  return (
    <div className="float-btns">
      <a
        href={WHATSAPP}
        className="float-btn float-wa"
        aria-label="WhatsApp"
      >
        <FaWhatsapp />
      </a>
      <a
        href={`tel:${PHONE}`}
        className="float-btn float-phone"
        aria-label="Ligar"
      >
        <FaPhoneAlt />
      </a>
    </div>
  );
}

// ─── APP ────────────────────────────────────────────────

export default function App() {
  useScrollReveal(".how-step, .testimonial-card, .coverage-tag");

  return (
    <>
      <Nav />
      <Hero />
      <Ticker />
      <Images />
      <HowItWorks />
      <Testimonials />
      <CtaFull />
      <Coverage />
      <Footer />
      <FloatBtn />
    </>
  );
}
